import {
  useSensor,
  useSensors,
  PointerSensor,
} from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { useCallback, useEffect, useRef, useState } from 'react';
import { siblingPointerCollision } from './siblingPointerCollision';
import {
  DROP_FLASH_MS,
  collectDescendantIds,
  findNodeById,
  isSameParent,
} from './dragFeedback';

/** Matches @dnd-kit/core MeasuringStrategy.Always without relying on the enum export in tests. */
const MEASURING_STRATEGY_ALWAYS = 'always';

/**
 * DnD sensors + drag-end handler for sibling-only module reorder.
 * @param {{
 *  enabled: boolean,
 *  onReorder: (parentId: string|null, orderedIds: string[]) => void,
 *  treeData?: Array,
 *  expanded?: string[],
 *  setExpanded?: (ids: string[]) => void,
 * }} options
 */
export function useModuleReorder({
  enabled,
  onReorder,
  treeData = [],
  expanded = [],
  setExpanded = () => {},
}) {
  const [activeId, setActiveId] = useState(null);
  const [activeLabel, setActiveLabel] = useState('');
  const [activeParentId, setActiveParentId] = useState(null);
  const [dropFlashId, setDropFlashId] = useState(null);
  const collapsedForDragRef = useRef([]);
  const expandedSnapshotRef = useRef(expanded);
  const flashTimerRef = useRef(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  useEffect(() => {
    expandedSnapshotRef.current = expanded;
  }, [expanded]);

  useEffect(() => () => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
  }, []);

  const collapseParentForDrag = useCallback((nodeId) => {
    const id = String(nodeId);
    const node = findNodeById(treeData, id);
    const currentExpanded = expandedSnapshotRef.current;
    const hasOpenChildren = collectDescendantIds(node).length > 0
      && currentExpanded.map(String).includes(id);

    if (!hasOpenChildren) {
      collapsedForDragRef.current = [];
      return;
    }

    collapsedForDragRef.current = [id];
    setExpanded(currentExpanded.filter((expandedId) => String(expandedId) !== id));
  }, [treeData, setExpanded]);

  const restoreCollapsedForDrag = useCallback(() => {
    const toRestore = collapsedForDragRef.current;
    collapsedForDragRef.current = [];
    if (!toRestore.length) return;

    const currentExpanded = expandedSnapshotRef.current;
    const merged = [
      ...currentExpanded,
      ...toRestore.filter((id) => !currentExpanded.map(String).includes(String(id))),
    ];
    setExpanded(merged);
  }, [setExpanded]);

  const flashDroppedRow = useCallback((nodeId) => {
    if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    setDropFlashId(String(nodeId));
    flashTimerRef.current = setTimeout(() => {
      setDropFlashId(null);
      flashTimerRef.current = null;
    }, DROP_FLASH_MS);
  }, []);

  const handleDragStart = useCallback((event) => {
    if (!enabled) return;
    const { active } = event;
    const nodeId = String(active.id);
    setActiveId(nodeId);
    setActiveLabel(active.data?.current?.label || '');
    setActiveParentId(active.data?.current?.parentId ?? null);
    collapseParentForDrag(nodeId);
  }, [enabled, collapseParentForDrag]);

  const clearDragState = useCallback(() => {
    setActiveId(null);
    setActiveLabel('');
    setActiveParentId(null);
  }, []);

  const handleDragEnd = useCallback((event) => {
    restoreCollapsedForDrag();

    if (!enabled) {
      clearDragState();
      return;
    }

    const { active, over } = event;
    clearDragState();

    if (!over || active.id === over.id) return;

    const sourceParentId = active.data?.current?.parentId ?? null;
    const overParentId = over.data?.current?.parentId ?? null;

    if (!isSameParent(sourceParentId, overParentId)) {
      return;
    }

    const siblingIds = active.data?.current?.siblingIds;
    if (!Array.isArray(siblingIds) || siblingIds.length === 0) return;

    const oldIndex = siblingIds.indexOf(String(active.id));
    const newIndex = siblingIds.indexOf(String(over.id));
    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

    const orderedIds = arrayMove(siblingIds, oldIndex, newIndex);
    onReorder?.(sourceParentId, orderedIds);
    flashDroppedRow(active.id);
  }, [enabled, onReorder, clearDragState, restoreCollapsedForDrag, flashDroppedRow]);

  const handleDragCancel = useCallback(() => {
    restoreCollapsedForDrag();
    clearDragState();
  }, [clearDragState, restoreCollapsedForDrag]);

  return {
    sensors,
    collisionDetection: siblingPointerCollision,
    measuring: {
      droppable: { strategy: MEASURING_STRATEGY_ALWAYS },
      draggable: { strategy: MEASURING_STRATEGY_ALWAYS },
    },
    activeId,
    activeLabel,
    activeParentId,
    dropFlashId,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    dndEnabled: enabled,
  };
}
